import moment from "moment";
import {
  TableCellType,
  TableHeader,
} from "../../../components/custom-table/table";
import { FilterValues } from "../../../data-model/filterValues";
import { NycbsPharmaOrderModel } from "../../../data-model/nycbsPharmaOrder";
import {
  TableCellButtonWithThreeDots,
  TableCellMultiline,
  TableCellWithBadge,
} from "../../../data-model/tableCells";
import { AsteraOncologySanityTableDataModel } from "../../../data-model/tablesData";
import {
  getKeyFromEnumValuesForOrderStatus,
  getOrderStatusDisplayText,
  OrderStatus,
} from "../../../enums/authStatus";
import { BadgeColor, BadgeTextColor } from "../../../enums/coloredText";
import {
  NycbsPharmaOrderKeys,
  NycbsPharmaOrderRequiredKeys,
  NycbsPharmaOrderRequiredKeysLabelForUI,
} from "../../../enums/nycbsPharmaOrder";
import { CmmOrderTableColumnKeys } from "../../../enums/tableColumnKeys";
import { calculateAge } from "../../../utils/ageCalculator";
import { filterData } from "../../../utils/filterData";
import { generateFullName } from "../../../utils/generateOrderDataForTable";
import { capitalizeString } from "../../../utils/stringModifications";
import { findLabelFromGivenState } from "../../pharmaPaForm/utils/stateOptions";
import { getButtonConfig } from "../utils/getButtonLabel";
import { getCorrectTextForHover } from "../utils/getCorrectTextForHover";
import { getPortalNameFromMetadata } from "./utils/getPortalNameFromMetadata";

/**
 * Returns a trimmed review-reason string for Needs Review column tooltips.
 * Accepts snake_case or camelCase field names from the API.
 *
 * @param item - Raw NYCBS / CMM pharma order row
 * @returns Non-empty trimmed text, or undefined when absent
 */
const getReviewReasonForTooltip = (
  item: NycbsPharmaOrderModel,
): string | undefined => {
  const snake = item?.[NycbsPharmaOrderKeys.ReviewReason];
  const camel = item?.["reviewReason"];
  const raw =
    typeof snake === "string" && snake.trim() !== ""
      ? snake
      : typeof camel === "string"
        ? camel
        : "";
  const trimmed = typeof raw === "string" ? raw.trim() : "";
  return trimmed.length > 0 ? trimmed : undefined;
};

export const AsteraOncologySanityTableHeader: TableHeader[] = [
  {
    label: "",
    key: "expandableRowIcon",
    order: 0,
    width: 3,
    type: TableCellType.EXPANDABLE_ROW_ICON,
  },
  {
    label: "Patient Details",
    key: CmmOrderTableColumnKeys.PATIENT_DETAILS,
    order: 1,
    width: 15,
    sortable: true,
    type: TableCellType.MULTILINE,
    subKey: "mainText",
  },
  {
    label: "Date of Birth",
    key: CmmOrderTableColumnKeys.DATE_OF_BIRTH,
    order: 2,
    width: 10,
    sortable: true,
    type: TableCellType.MULTILINE,
    subKey: "mainText",
  },
  {
    label: "Date of Service",
    key: CmmOrderTableColumnKeys.DATE_OF_SERVICE,
    order: 3,
    width: 10,
    sortable: true,
    type: TableCellType.STRING,
  },

  {
    label: "Provider Details",
    key: CmmOrderTableColumnKeys.PROVIDER_DETAILS,
    order: 4,
    width: 10,
    sortable: false,
    type: TableCellType.STRING,
  },
  {
    label: "Drug",
    key: CmmOrderTableColumnKeys.DRUG,
    order: 5,
    width: 15,
    sortable: false,
    type: TableCellType.MULTILINE,
  },
  // {
  //   label: "Type",
  //   key: CmmOrderTableColumnKeys.TYPE,
  //   order: 6,
  //   width: 6,
  //   sortable: false,
  //   type: TableCellType.STRING,
  // },
  {
    label: "Key",
    key: CmmOrderTableColumnKeys.CMM_KEY,
    order: 6,
    width: 10,
    sortable: false,
    type: TableCellType.COPY_DATA,
  },
  {
    label: "Missing Data",
    key: CmmOrderTableColumnKeys.NO_DATA_FIELDS,
    order: 7,
    width: 7,
    sortable: false,
    type: TableCellType.BADGE,
  },

  {
    label: "CMM Status",
    key: CmmOrderTableColumnKeys.CMM_STATUS,
    order: 8,
    width: 8,
    sortable: false,
    type: TableCellType.BADGE,
  },
  {
    label: "Needs Review",
    key: CmmOrderTableColumnKeys.NEEDS_REVIEW,
    order: 9,
    width: 7,
    sortable: false,
    type: TableCellType.BADGE,
  },
  // {
  //   label: "Portal",
  //   key: CmmOrderTableColumnKeys.PORTAL,
  //   order: 10,
  //   width: 6,
  //   sortable: false,
  //   type: TableCellType.BADGE,
  // },
  {
    label: "Action",
    key: CmmOrderTableColumnKeys.ACTION,
    order: 10,
    width: 11,
    sortable: false,
    type: TableCellType.BUTTON_WITH_THREE_DOTS,
  },
  {
    label: "CMM",
    key: CmmOrderTableColumnKeys.CMM_INPUT_VIEW_ICON,
    order: 11,
    width: 4,
    sortable: false,
    type: TableCellType.CMM_INPUT_VIEW_ICON,
  },
];

export const generateTableDataForAsteraOncologySanityTable = (
  data: NycbsPharmaOrderModel[],
  getDrugColor?: (drugName: string | undefined | null) => string,
): AsteraOncologySanityTableDataModel[] => {
  return data.map((item) => {
    const noDataFields: string[] = generateFieldNamesWhichHaveNoValue(item);

    const patientDetails: TableCellMultiline = {
      mainText: capitalizeString(
        generateFullName(
          item?.[NycbsPharmaOrderKeys.PatientFirstName] ?? "",
          "",
          item?.[NycbsPharmaOrderKeys.PatientLastName] ?? "",
        ),
      ),
      secondaryText: `MRN : ${item?.[NycbsPharmaOrderKeys.PatientMrn] ?? "--"}`,
    };
    const dateOfBirth: TableCellMultiline = {
      mainText: item?.[NycbsPharmaOrderKeys.PatientDob] ?? "--",
      secondaryText: `${calculateAge(item?.[NycbsPharmaOrderKeys.PatientDob] ?? "--")} yrs`,
      hideCopyIcon: true,
    };
    const dateOfService: string = item?.[NycbsPharmaOrderKeys.CreatedAt]
      ? moment(item?.[NycbsPharmaOrderKeys.CreatedAt]).format("MM/DD/YYYY")
      : "--";
    const medication: string =
      item?.[NycbsPharmaOrderKeys.DrugNameOncoEmr] ?? "--";
    const providerDetails: string = capitalizeString(
      generateFullName(
        item?.[NycbsPharmaOrderKeys.ProviderFirstName] ?? "",
        "",
        item?.[NycbsPharmaOrderKeys.ProviderLastName] ?? "",
      ),
    );

    // const dateOfBirth: string = item?.[NycbsPharmaOrderKeys.PatientDob] ?? "--";

    const insuranceState: string =
      findLabelFromGivenState(
        item?.[NycbsPharmaOrderKeys.PatientInsuranceState] ?? "",
      ) ?? "--";

    const drug: TableCellMultiline = {
      mainText: item?.[NycbsPharmaOrderKeys.DrugNameOncoEmr] ?? "--",
      secondaryText: item?.[NycbsPharmaOrderKeys.NdcNumber] ?? "--",
    };

    const id = item?.[NycbsPharmaOrderKeys.Identifier] ?? "";

    const metadata = item?.[NycbsPharmaOrderKeys.Metadata];
    const portalName = getPortalNameFromMetadata(metadata);
    const isEvicore = portalName?.toLowerCase() === "evicore";
    const portal: TableCellWithBadge = {
      text: portalName,
      color: isEvicore
        ? BadgeTextColor["Approved"]
        : BadgeTextColor["First Step Outcome"],
      bgColor: isEvicore
        ? BadgeColor["Approved"]
        : BadgeColor["First Step Outcome"],
    };

    const buttonConfig = getButtonConfig(
      item?.[NycbsPharmaOrderKeys.CmmStatus] ?? "",
      noDataFields.length > 0, // Pass true if there's missing data
    );

    const button: TableCellButtonWithThreeDots = {
      label: buttonConfig.label,
      buttonId: buttonConfig.buttonId,
      disabled: buttonConfig.disabled ?? false,
      rowId: id,
      threeDotsOptions: [
        {
          id: "rerun_from_onco_emr",
          text: "Rerun from Onco Emr",
        },
        {
          id: "rerun_from_cmm",
          text: "Rerun from CMM",
        },
        {
          id: "report_prescription_inaccuracy",
          text: "Report Prescription Inaccuracy",
        },
        {
          id: "report_medication_inaccuracy",
          text: "Report Medication Inaccuracy",
        },
        {
          id: "report_inaccuracy",
          text: "Report Form Inaccuracy",
        },
      ],
    };

    const cmmStatusKey = getKeyFromEnumValuesForOrderStatus(
      item?.[NycbsPharmaOrderKeys.CmmStatus] ?? "",
    );
    const hoverText = getCorrectTextForHover(item);

    const cmmStatusText = cmmStatusKey
      ? OrderStatus[cmmStatusKey]
      : "No Status";
    const badgeText = {
      text: getOrderStatusDisplayText(cmmStatusText),
      color: BadgeTextColor[cmmStatusText],
      bgColor: BadgeColor[cmmStatusText],
      hoverText: hoverText,
    };

    const noDataFieldsBadge: TableCellWithBadge = {
      text: noDataFields.length > 0 ? `Yes (${noDataFields.length})` : "No",
      color:
        noDataFields.length > 0
          ? BadgeTextColor["Error"]
          : BadgeTextColor["Approved"],
      bgColor:
        noDataFields.length > 0 ? BadgeColor["Error"] : BadgeColor["Approved"],
      displayText:
        noDataFields.length > 0 ? noDataFields.join(", ") : "No Missing Fields",
    };
    const needsReviewRaw = item?.[NycbsPharmaOrderKeys.NeedsReview];
    const needsReviewNormalized =
      typeof needsReviewRaw === "string"
        ? needsReviewRaw.trim().toLowerCase()
        : "";
    const needsReviewYes = needsReviewNormalized === "needed";
    const reviewReasonTooltip = getReviewReasonForTooltip(item);
    const needsReviewBadge: TableCellWithBadge = {
      text: needsReviewYes ? "Yes" : "No",
      color: needsReviewYes
        ? BadgeTextColor["Error"]
        : BadgeTextColor["Approved"],
      bgColor: needsReviewYes ? BadgeColor["Error"] : BadgeColor["Approved"],
      hoverText: reviewReasonTooltip,
      displayText: reviewReasonTooltip,
    };
    const cmmKey = item?.[NycbsPharmaOrderKeys.CmmResultKey] ?? "";
    const formName = item?.[NycbsPharmaOrderKeys.FormName] ?? "";
    const username = item?.[NycbsPharmaOrderKeys.PortalUsername] ?? "";

    // Additional fields for expandable row
    const drugQuantity = item?.[NycbsPharmaOrderKeys.DrugQuantity] ?? "";
    const drugDaysSupply = item?.[NycbsPharmaOrderKeys.DrugDaysSupply] ?? "";
    const drugQuantityQualifier =
      item?.[NycbsPharmaOrderKeys.DrugQuantityQualifier] ?? "";

    const primaryDiagnoses =
      item?.[NycbsPharmaOrderKeys.PrimaryDiagnoses] ?? "";
    const primaryDiagnosesDescription =
      item?.[NycbsPharmaOrderKeys.PrimaryDiagnosesDescription] ?? "";

    const secondaryDiagnoses =
      item?.[NycbsPharmaOrderKeys.SecondaryDiagnoses] ?? "";
    const secondaryDiagnosesDescription =
      item?.[NycbsPharmaOrderKeys.SecondaryDiagnosesDescription] ?? "";

    const planName = item?.[NycbsPharmaOrderKeys.PlanName] ?? "";
    const patientRxBin = item?.[NycbsPharmaOrderKeys.PatientRxBin] ?? "";
    const patientRxGroup = item?.[NycbsPharmaOrderKeys.PatientRxGroup] ?? "";
    const patientRxPcn = item?.[NycbsPharmaOrderKeys.PatientRxPcn] ?? "";
    const formPickedFlag = item?.[NycbsPharmaOrderKeys.FormPickedFlag] ?? "";
    const patientMemberId = item?.[NycbsPharmaOrderKeys.PatientMemberId] ?? "";
    const drugConfidenceScore =
      item?.[NycbsPharmaOrderKeys.DrugConfidenceScore] ?? "";
    const drugPickedThinking =
      item?.[NycbsPharmaOrderKeys.DrugPickedThinking] ?? "";
    const drugFetchedFrom = item?.[NycbsPharmaOrderKeys.DrugFetchedFrom] ?? "";
    const type = item?.[NycbsPharmaOrderKeys.Type] ?? "--";
    const primaryDiagnosesData =
      item?.[NycbsPharmaOrderKeys.PrimaryDiagnosesData] ?? {};
    const secondaryDiagnosesData =
      item?.[NycbsPharmaOrderKeys.SecondaryDiagnosesData] ?? {};
    const prescriptionData =
      item?.[NycbsPharmaOrderKeys.PrescriptionData] ?? {};
    const drugName = item?.[NycbsPharmaOrderKeys.DrugName] ?? "";

    const drugNameForColor = item?.[NycbsPharmaOrderKeys.DrugNameOncoEmr];
    const borderColor = getDrugColor ? getDrugColor(drugNameForColor) : "gray";
    const activeInsurance = item?.[NycbsPharmaOrderKeys.ActiveInsurance] ?? {};

    return {
      patientDetails,
      dateOfService,
      medication,
      providerDetails,
      dateOfBirth,
      insuranceState,
      drug,
      button,
      [CmmOrderTableColumnKeys.CMM_STATUS]: badgeText,
      id,
      cmmKey,
      formName,
      cmmOrderDeleteIcon: id,
      patientId: item?.[NycbsPharmaOrderKeys.PatientMrn] ?? "",
      rowData: JSON.stringify(item),
      noDataFields: noDataFieldsBadge,
      needsReview: needsReviewBadge,
      username,
      expandableRowIcon: {
        borderColor: borderColor,
        borderWidth: 4,
        id: item?.[NycbsPharmaOrderKeys.Identifier] ?? "",
        isExpanded: false,
      },
      drugQuantity,
      drugDaysSupply,
      drugQuantityQualifier,
      primaryDiagnoses,
      primaryDiagnosesDescription,
      secondaryDiagnoses,
      secondaryDiagnosesDescription,
      planName,
      patientRxBin,
      patientRxGroup,
      patientRxPcn,
      patientMemberId,
      formPickedFlag,
      drugConfidenceScore,
      drugPickedThinking,
      drugFetchedFrom,
      type,
      primaryDiagnosesData,
      secondaryDiagnosesData,
      prescriptionData,
      cmmInputViewIcon: id,
      drugName,
      activeInsurance,
      portal,
      sftpRxBin: item?.[NycbsPharmaOrderKeys.SftpRxBin] ?? "",
      metadata,
    };
  });
};

export const generateFilterDataForAsteraOncologySanityTable = (
  data: NycbsPharmaOrderModel[],
  filterValues: FilterValues,
) => {
  const filteredData = filterData(data, filterValues);
  return filteredData;
};

export const generateFieldNamesWhichHaveNoValue = (
  data: NycbsPharmaOrderModel,
) => {
  const fieldNames: string[] = [];
  for (const key in NycbsPharmaOrderRequiredKeys) {
    const valueMapping = NycbsPharmaOrderRequiredKeys[key];
    if (valueMapping === NycbsPharmaOrderKeys.PrimaryDiagnoses) {
      const dataRequiredForPrimaryDiagnosis =
        data?.[NycbsPharmaOrderKeys.IsDiagnosisCodeAvailableOnForm];
      // Defensive check: exclude both string "false" and boolean false
      // While type is string|null, runtime data may contain boolean values
      if (
        dataRequiredForPrimaryDiagnosis !== "false" &&
        // @ts-ignore - Defensive check for boolean despite string|null type
        dataRequiredForPrimaryDiagnosis !== false &&
        (data[valueMapping] === undefined ||
          data?.[valueMapping] === "" ||
          data?.[valueMapping] === null)
      ) {
        fieldNames.push(NycbsPharmaOrderRequiredKeysLabelForUI[key]);
      }
    } else if (valueMapping === NycbsPharmaOrderKeys.FormName) {
      const formName = data?.[valueMapping];
      if (
        formName === undefined ||
        formName === null ||
        formName === "" ||
        formName?.toLowerCase().includes("no forms available")
      ) {
        fieldNames.push(NycbsPharmaOrderRequiredKeysLabelForUI[key]);
      }
    } else if (
      data?.[valueMapping] === undefined ||
      data?.[valueMapping] === null ||
      data?.[valueMapping] === "" ||
      data?.[valueMapping]?.toString()?.trim() === "0"
    ) {
      fieldNames.push(NycbsPharmaOrderRequiredKeysLabelForUI[key]);
    }
  }
  return fieldNames;
};
